<template>
    <b-modal ref="modal" centered no-close-on-backdrop no-close-on-esc hide-header hide-footer size="lg">
        <div class="modal-header">
            <div class="modal-title">
                <h5>{{ formData && (formData.id || formData.notificationRuleId) ? $t('EDITNOTIFICATIONRULE') : $t('NEWNOTIFICATIONRULE') }}</h5>
            </div>
            <button type="button" aria-label="Close" @click="close()" class="close">
                <img src="../../../assets/img/icon-close.svg">
            </button>
        </div>

        <div class="d-block mt-2 modal-content-inside" v-if="formData != null">
            <!-- Step Navigation Tabs / Wizard Header -->
            <div class="wizard-steps d-flex justify-content-between mb-4 border-bottom pb-2">
                <div 
                    class="wizard-step flex-fill text-center p-2"
                    :class="{ 'active': currentStep === 1, 'cursor-pointer': 1 <= maxStepReached, 'disabled-tab': 1 > maxStepReached }"
                    @click="goToStep(1)">
                    {{$t('STEP_INTERVAL')}}
                </div>
                <div 
                    class="wizard-step flex-fill text-center p-2"
                    :class="{ 'active': currentStep === 2, 'cursor-pointer': 2 <= maxStepReached, 'disabled-tab': 2 > maxStepReached }"
                    @click="goToStep(2)">
                    {{$t('STEP_OBJECTS')}}
                </div>
                <div 
                    class="wizard-step flex-fill text-center p-2"
                    :class="{ 'active': currentStep === 3, 'cursor-pointer': 3 <= maxStepReached, 'disabled-tab': 3 > maxStepReached }"
                    @click="goToStep(3)">
                    {{$t('STEP_STATUSES')}}
                </div>
                <div 
                    class="wizard-step flex-fill text-center p-2"
                    :class="{ 'active': currentStep === 4, 'cursor-pointer': 4 <= maxStepReached, 'disabled-tab': 4 > maxStepReached }"
                    @click="goToStep(4)">
                    {{$t('STEP_PERIOD')}}
                </div>
            </div>

            <!-- FIXED HEIGHT STEP CONTAINER -->
            <div class="wizard-step-body">
                <!-- STEP 1: INTERVAL -->
                <div v-show="currentStep === 1">
                    <div class="form-row">
                        <div class="col-sm-6">
                            <b-form-fieldset v-bind:label="$t('RECURRINGTYPE')">
                                <inputvalidation :formerror="formError" name="recurringType">
                                    <b-form-select :options="dateType" v-model="formData.recurringType"></b-form-select>
                                </inputvalidation>
                            </b-form-fieldset>
                        </div>
                        <div class="col-sm-6">
                            <b-form-fieldset v-bind:label="getIntervalLabel">
                                <inputvalidation :formerror="formError" name="interval">
                                    <b-form-select 
                                        v-if="formData.recurringType === 'day_of_week'" 
                                        multiple 
                                        :select-size="7" 
                                        :options="daysOfWeek" 
                                        v-model="intervalDays">
                                    </b-form-select>
                                    <b-form-input 
                                        v-else-if="formData.recurringType === 'day_of_month'" 
                                        type="text" 
                                        placeholder="e.g. 1, 15, 30"
                                        v-model="formData.interval">
                                    </b-form-input>
                                    <b-form-input 
                                        v-else 
                                        type="number" 
                                        v-model="formData.interval">
                                    </b-form-input>
                                </inputvalidation>
                            </b-form-fieldset>
                        </div>
                    </div>
                </div>

                <!-- STEP 2: OBJECT FILTERING -->
                <div v-show="currentStep === 2">
                    <div class="form-row" v-if="globals && (globals.groupId === 1 || globals.groupId === 5)">
                        <div class="col-sm-12 mb-3">
                            <b-form-fieldset v-bind:label="$t('USERCOMPANIES') || $t('USERSUBCOMPANIES') || 'Property Manager User'">
                                <inputvalidation :formerror="formError" name="userId">
                                    <lbselect 
                                      :empty="true"
                                      :list="pmUsers.root" 
                                      :total-count="pmUsers.totalCount"
                                      :pagination="pmUsersPagination"
                                      v-on:reload="getPmUsers()"
                                      v-model="formData.userId" 
                                      value-field="userId" 
                                      :display-func="(item) => getPmUserDisplayName(item)">
                                    </lbselect>
                                </inputvalidation>
                            </b-form-fieldset>
                        </div>
                    </div>
                    <div class="form-row">
                        <div class="col-sm-12">
                            <b-form-fieldset v-bind:label="$t('BUILDING')">
                                <inputvalidation :formerror="formError" name="buildingDetailId">
                                    <lbselect 
                                      :empty="true"
                                      :multiselect="true"
                                      :list="buildingDetail.root" 
                                      :total-count="buildingDetail.totalCount"
                                      :pagination="buildingDetailPagination"
                                      v-on:reload="getBuildingDetail()"
                                      v-model="formData.buildingDetailId" 
                                      value-field="buildingDetailId" 
                                      :display-func="(item) => getBuildingDetailName(item)">
                                    </lbselect>
                                </inputvalidation>
                            </b-form-fieldset>
                        </div>
                    </div>
                </div>

                <!-- STEP 3: STATUS & JOB CREATED FILTERING -->
                <div v-show="currentStep === 3">
                    <div class="form-row mb-3">
                        <div class="col-sm-12">
                            <b-form-fieldset v-bind:label="$t('STATUS')">
                                <inputvalidation :formerror="formError" name="jobStatus">
                                    <div class="d-flex flex-wrap pt-2">
                                        <b-form-checkbox 
                                            v-for="statusOpt in statusOptions" 
                                            :key="statusOpt.value" 
                                            v-model="selectedStatuses" 
                                            :value="statusOpt.value" 
                                            class="mr-4 mb-3">
                                            <span :class="`badge badge-${getStatusBadgeColor(statusOpt.value)} p-2`">
                                                {{ statusOpt.text.toUpperCase() }}
                                            </span>
                                        </b-form-checkbox>
                                    </div>
                                </inputvalidation>
                            </b-form-fieldset>
                        </div>
                    </div>
                    <div class="form-row border-top pt-3">
                        <div class="col-sm-6">
                            <b-form-fieldset v-bind:label="$t('JOBCREATEDSTARTDATE') || 'Job Created Start Date'">
                                <inputvalidation :formerror="formError" name="jobCreatedStartDate">
                                    <datetimepicker 
                                        type="date"
                                        v-model="formData.jobCreatedStartDate"
                                        format="YYYY-MM-DD"
                                        display-format="DD.MM.YYYY."
                                        :isClearable="true"
                                        :simple="true"
                                        :translate-method="trans">
                                    </datetimepicker>
                                </inputvalidation>
                            </b-form-fieldset>
                        </div>
                        <div class="col-sm-6">
                            <b-form-fieldset v-bind:label="$t('JOBCREATEDENDDATE') || 'Job Created End Date'">
                                <inputvalidation :formerror="formError" name="jobCreatedEndDate">
                                    <datetimepicker 
                                        type="date"
                                        v-model="formData.jobCreatedEndDate"
                                        format="YYYY-MM-DD"
                                        display-format="DD.MM.YYYY."
                                        :isClearable="true"
                                        :simple="true"
                                        :translate-method="trans">
                                    </datetimepicker>
                                </inputvalidation>
                            </b-form-fieldset>
                        </div>
                    </div>
                </div>

                <!-- STEP 4: FROM - TO PERIOD -->
                <div v-show="currentStep === 4">
                    <div class="form-row">
                        <div class="col-sm-6">
                            <b-form-fieldset v-bind:label="$t('STARTDATE')">
                                <inputvalidation :formerror="formError" name="startDate">
                                    <datetimepicker 
                                        type="date"
                                        v-model="formData.startDate"
                                        format="YYYY-MM-DD"
                                        display-format="DD.MM.YYYY."
                                        :simple="true"
                                        :translate-method="trans">
                                    </datetimepicker>
                                </inputvalidation>
                            </b-form-fieldset>
                        </div>
                        <div class="col-sm-6">
                            <b-form-fieldset v-bind:label="$t('ENDDATE')">
                                <inputvalidation :formerror="formError" name="endDate">
                                    <datetimepicker 
                                        type="date"
                                        v-model="formData.endDate"
                                        format="YYYY-MM-DD"
                                        display-format="DD.MM.YYYY."
                                        :isClearable="true"
                                        :simple="true"
                                        :translate-method="trans">
                                    </datetimepicker>
                                </inputvalidation>
                            </b-form-fieldset>
                        </div>
                    </div>
                    <div class="form-row mt-2">
                        <div class="col-sm-12">
                            <b-form-fieldset v-bind:label="$t('ISACTIVE')">
                                <inputvalidation :formerror="formError" name="isActive">
                                    <b-form-checkbox v-model="formData.isActive" :value="1" :unchecked-value="0">
                                        {{$t('ISACTIVE')}}
                                    </b-form-checkbox>
                                </inputvalidation>
                            </b-form-fieldset>
                        </div>
                    </div>
                </div>
            </div>

            <!-- FOOTER BUTTONS -->
            <div class="text-center mt-4">
                <b-btn class="mr-2 btn-bis btn-cancel" @click="close()">{{$t('CANCEL')}}</b-btn>
                <b-btn class="mr-2 btn-bis btn-secondary" :disabled="currentStep === 1" @click="prevStep()">
                    {{$t('PREVIOUS') || 'Previous'}}
                </b-btn>
                <b-btn class="btn-bis btn-primary" v-if="currentStep < 4" @click="nextStep()">
                    {{$t('NEXT') || 'Next'}}
                </b-btn>
                <b-btn class="btn-bis btn-primary" v-if="currentStep === 4" @click="submitData(formData)">
                    {{$t('SAVE')}}
                </b-btn>
            </div>
        </div>
    </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import Api from '@/api/notification_center/NotificationRule.js';
import BuildingDetailApi from '@/api/building_detail/BuildingDetail.js';
import UserApi from '@/api/admin/Users.js';
import localTranslate from '../../helpers/local_trans';

export default {
    name: 'NotificationRuleForm',
    props: ['value'],
    data() {
        return {
            currentStep: 1,
            maxStepReached: 1,
            buildingDetail: {},
            pmUsers: {},
            formError: {},
            formData: null,
            buildingDetailPagination: {
                query: '',
                currentPage: 1,
                initialId: [],
                limit: 25,
                searchTimeout: false,
            },
            pmUsersPagination: {
                query: '',
                currentPage: 1,
                initialId: [],
                limit: 25,
                searchTimeout: false,
            },
        };
    },
    methods: {
        trans(key) {
            return localTranslate(this.globals.langId, key);
        },
        getBuildingDetailName(item) {
            return `${item.clientName} ${item.address}`;
        },
        getPmUserDisplayName(item) {
            if (!item) return '';
            const name = `${item.name || ''} ${item.surname || ''}`.trim();
            const companyName = item.companyName || item.subCompanyName;
            if (companyName) {
                return `${companyName} (${name || item.username || ''})`;
            }
            return name || item.username;
        },
        getStatusBadgeColor(status) {
            switch (status) {
                case 'finished': return 'success';
                case 'partialyfinished': return 'info';
                case 'canceled': return 'danger';
                case 'overdue': return 'warning';
                default: return 'secondary';
            }
        },
        goToStep(step) {
            if (step <= this.maxStepReached) {
                this.currentStep = step;
            }
        },
        nextStep() {
            if (this.currentStep < 4) {
                this.currentStep++;
                if (this.currentStep > this.maxStepReached) {
                    this.maxStepReached = this.currentStep;
                }
            }
        },
        prevStep() {
            if (this.currentStep > 1) {
                this.currentStep--;
            }
        },
        async submitData(item) {
            if (this.currentStep !== 4) return;
            try {
                if (item.notificationRuleId) {
                    item.id = item.notificationRuleId;
                }

                const data = await Api.save(item);
                this.$notify.notice(data);

                if (data.error) {
                    this.formError = data;
                } else {
                    this.close();
                }
            } catch (e) {
                console.log(e);
                this.$notify.notice(e);
            }
        },
        async getBuildingDetail() {
            try {
                const data = await BuildingDetailApi.selectRow(this.getBuildingDetailParams);
                if (data.error && data.error === true) {
                    throw data;
                }
                this.buildingDetail = data;
            } catch (error) {
                this.$notify.notice(error);
            }
        },
        async getPmUsers() {
            try {
                const params = {
                    conditions: JSON.stringify({ groupId: 6 }),
                    query: this.pmUsersPagination.query,
                    start: (this.pmUsersPagination.currentPage - 1) * this.pmUsersPagination.limit,
                    limit: this.pmUsersPagination.limit,
                };
                if (this.pmUsersPagination.initialId && this.pmUsersPagination.initialId.length) {
                    params.initialId = this.pmUsersPagination.initialId;
                }
                const data = await UserApi.selectRow(params);
                if (data.error && data.error === true) {
                    throw data;
                }
                this.pmUsers = data;
            } catch (error) {
                this.$notify.notice(error);
            }
        },
        close() {
            this.formData = null;
            this.formError = {};
            this.currentStep = 1;
            this.maxStepReached = 1;
            this.$emit('close');
        },
    },
    watch: {
        async formData(val) {
            if (val != null) {
                await this.getBuildingDetail();
                if (this.globals && (this.globals.groupId === 1 || this.globals.groupId === 5)) {
                    this.pmUsersPagination.initialId = val.userId ? [val.userId] : [];
                    await this.getPmUsers();
                }
                this.$refs.modal.show();
            } else {
                this.$refs.modal.hide();
            }
        },
        value(val) {
            if (val != null) {
                this.currentStep = 1;
                this.maxStepReached = (val.id || val.notificationRuleId) ? 4 : 1;
                this.formData = {
                    recurringType: 'days',
                    interval: '1',
                    buildingDetailId: '',
                    jobStatus: 'finished,overdue',
                    startDate: new Date().toISOString().split('T')[0],
                    endDate: null,
                    jobCreatedStartDate: null,
                    jobCreatedEndDate: null,
                    isActive: 1,
                    userId: null,
                    ...val,
                };
            } else {
                this.formData = null;
                this.currentStep = 1;
                this.maxStepReached = 1;
            }
        },
    },
    computed: {
        ...mapGetters(['globals']),
        getIntervalLabel() {
            if (!this.formData) return this.$i18n.t('RECURRINGINTERVAL');
            if (this.formData.recurringType === 'day_of_week') return this.$i18n.t('DAYOFWEEK');
            if (this.formData.recurringType === 'day_of_month') return this.$i18n.t('DAYOFMONTH') || 'Day of the Month';
            return this.$i18n.t('RECURRINGINTERVAL');
        },
        dateType() {
            return [
                { value: 'days', text: this.$i18n.t('DAYS') },
                { value: 'weeks', text: this.$i18n.t('WEEKS') },
                { value: 'months', text: this.$i18n.t('MONTHS') },
                { value: 'years', text: this.$i18n.t('YEARS') },
                { value: 'day_of_week', text: this.$i18n.t('DAYOFWEEK') },
                { value: 'day_of_month', text: this.$i18n.t('DAYOFMONTH') || 'Day of the Month' },
            ];
        },
        daysOfWeek() {
            return [
                { value: 1, text: this.$i18n.t('MONDAY') },
                { value: 2, text: this.$i18n.t('TUESDAY') },
                { value: 3, text: this.$i18n.t('WEDNESDAY') },
                { value: 4, text: this.$i18n.t('THURSDAY') },
                { value: 5, text: this.$i18n.t('FRIDAY') },
                { value: 6, text: this.$i18n.t('SATURDAY') },
                { value: 7, text: this.$i18n.t('SUNDAY') },
            ];
        },
        statusOptions() {
            return [
                { value: 'finished', text: this.$i18n.t('FINISHED') },
                { value: 'partialyfinished', text: this.$i18n.t('PARTIALYFINISHED') },
                { value: 'canceled', text: this.$i18n.t('CANCELED') },
                { value: 'overdue', text: this.$i18n.t('OVERDUE') },
            ];
        },
        intervalDays: {
            get() {
                if (!this.formData || !this.formData.interval) return [];
                if (Array.isArray(this.formData.interval)) return this.formData.interval.map(Number);
                return this.formData.interval.toString().split(',').map(Number);
            },
            set(val) {
                if (this.formData) {
                    this.formData.interval = val.join(',');
                }
            },
        },
        selectedStatuses: {
            get() {
                if (!this.formData || !this.formData.jobStatus) return [];
                return this.formData.jobStatus.toString().split(',').map(s => s.trim()).filter(Boolean);
            },
            set(val) {
                if (this.formData) {
                    this.formData.jobStatus = val.join(',');
                }
            },
        },
        getBuildingDetailParams() {
            const data = { ...this.buildingDetailPagination };
            data.start = (data.currentPage - 1) * data.limit;
            return data;
        },
    },
};
</script>

<style scoped>
.wizard-steps .wizard-step {
    font-size: 14px;
    user-select: none;
    transition: all 0.2s ease;
    border-bottom: 2px solid transparent;
    color: #6c757d;
}
.wizard-steps .wizard-step.active {
    color: #20a8d8;
    font-weight: bold;
    border-bottom: 2px solid #20a8d8;
}
.wizard-steps .wizard-step.disabled-tab {
    opacity: 0.5;
    cursor: not-allowed;
}
.wizard-step-body {
    min-height: 250px;
    position: relative;
    padding-right: 5px;
}

/* Ensure lbselect dropdown menu floats above footer and scrolls internally */
.wizard-step-body ::v-deep .lb-vue-select-options,
.wizard-step-body ::v-deep .lb-vue-select-list,
.wizard-step-body ::v-deep .dropdown-menu,
.wizard-step-body ::v-deep ul.options {
    max-height: 180px !important;
    overflow-y: auto !important;
    z-index: 1050 !important;
}
</style>
